import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["intl", "viewAllLinkGenerator", "codeSearchUrlGenerator", "codeSearchContextualUrlGenerator", "urlGeneratorForNoResultsScreen", "permissionSupplier", "preQueryItemSupplier", "postQueryItemSupplier", "currentContext", "features"];
import React, { useMemo, useCallback } from 'react';
import { injectIntl } from 'react-intl-next';
import { SearchFooter } from '@atlassian/search-dialog';
import { useDefaultSuppliers } from '../..';
import { messages } from './messages';
import { messages as commonMessages } from '../../../messages';
import { AdvancedSearch } from '../../advanced-search/advanced-search';
import { SearchDialogProduct, useProductContext } from '../../product-router';
import { useBitbucketPermissionSupplier } from './bitbucket-permission-supplier';
import { BitbucketScope, BITBUCKET_PRODUCT_ID } from './types';
import { bitbucketURLGenerators } from './url-generators';
import { useShowAtlassianHomeSearchLink } from '../../../common/show-atlassian-home-search-link';
import { AtlassianHomeSearchLink, getAtlassianHomeSearchUrl } from '../../advanced-search-footer/atlassian-home-search-link';
import { useUserContext } from '../../../common/user-context';
import { useTypedAggregatorClient } from '../../aggregator-client-context';
var mapAggregatorResponseToSearchItem = function mapAggregatorResponseToSearchItem(repo) {
  return {
    title: repo.name,
    id: repo.uuid,
    meta: repo.workspace.name,
    url: repo.links.html.href,
    iconUrl: repo.links.avatar.href,
    containerId: repo.workspace.uuid
  };
};
export var BitbucketTabWithIntl = function BitbucketTabWithIntl(_ref) {
  var intl = _ref.intl,
    overriddenViewAllLinkGenerator = _ref.viewAllLinkGenerator,
    overriddenCodeSearchUrlGenerator = _ref.codeSearchUrlGenerator,
    overriddenCodeSearchContextualUrlGenerator = _ref.codeSearchContextualUrlGenerator,
    overriddenUrlGeneratorForNoResultsScreen = _ref.urlGeneratorForNoResultsScreen,
    overriddenPermissionsSupplier = _ref.permissionSupplier,
    preQueryItemSupplier = _ref.preQueryItemSupplier,
    postQueryItemSupplier = _ref.postQueryItemSupplier,
    currentContext = _ref.currentContext,
    features = _ref.features,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useMemo = useMemo(function () {
      return bitbucketURLGenerators({
        viewAllLinkGenerator: overriddenViewAllLinkGenerator,
        codeSearchUrlGenerator: overriddenCodeSearchUrlGenerator,
        codeSearchContextualUrlGenerator: overriddenCodeSearchContextualUrlGenerator,
        urlGeneratorForNoResultsScreen: overriddenUrlGeneratorForNoResultsScreen
      });
    }, [overriddenCodeSearchUrlGenerator, overriddenCodeSearchContextualUrlGenerator, overriddenUrlGeneratorForNoResultsScreen, overriddenViewAllLinkGenerator]),
    viewAllLinkGenerator = _useMemo.viewAllLinkGenerator,
    codeSearchUrlGenerator = _useMemo.codeSearchUrlGenerator,
    codeSearchContextualUrlGenerator = _useMemo.codeSearchContextualUrlGenerator,
    urlGeneratorForNoResultsScreen = _useMemo.urlGeneratorForNoResultsScreen;
  var sections = [{
    id: BitbucketScope.BitbucketRepostitory,
    title: intl.formatMessage(messages.bitbucket_repository_heading),
    scope: BitbucketScope.BitbucketRepostitory,
    showResultCount: true,
    viewAllLinkGenerator: viewAllLinkGenerator,
    resultMapper: function resultMapper(_ref2) {
      var results = _ref2.results;
      return results.map(function (value) {
        return mapAggregatorResponseToSearchItem(value);
      });
    }
  }];
  var itemSuppliers = useDefaultSuppliers(BITBUCKET_PRODUCT_ID, sections, {
    preQueryItemSupplier: preQueryItemSupplier,
    postQueryItemSupplier: postQueryItemSupplier
  });
  var permissionSupplier = useBitbucketPermissionSupplier(overriddenPermissionsSupplier);
  var formattedSearchForCodeLabel = function formattedSearchForCodeLabel(context) {
    var _ref3 = context || {},
      repository = _ref3.repository,
      workspace = _ref3.workspace;
    var source = 'Bitbucket';
    if (repository) {
      source = repository.name;
    } else if (workspace) {
      source = workspace.name;
    }
    return intl.formatMessage(messages.bitbucket_advanced_search, {
      source: source
    });
  };
  var _useProductContext = useProductContext(),
    getPrimaryProduct = _useProductContext.getPrimaryProduct;
  var primaryProduct = getPrimaryProduct();
  var _useUserContext = useUserContext(),
    orgId = _useUserContext.orgId;
  var aggregatorClient = useTypedAggregatorClient();
  var cloudId = aggregatorClient === null || aggregatorClient === void 0 ? void 0 : aggregatorClient.getAggregatorConfig().cloudId;
  var showAtlassianHomeSearchLink = !!(useShowAtlassianHomeSearchLink(primaryProduct === null || primaryProduct === void 0 ? void 0 : primaryProduct.id) && orgId && cloudId);
  var generateAdvancedSearchUrl = useCallback(function (query, _, usage) {
    if (usage === 'onInputEnterModified' && showAtlassianHomeSearchLink) {
      return getAtlassianHomeSearchUrl(orgId, cloudId, query, primaryProduct === null || primaryProduct === void 0 ? void 0 : primaryProduct.id);
    }
    return viewAllLinkGenerator(query);
  }, [viewAllLinkGenerator, orgId, cloudId, showAtlassianHomeSearchLink, primaryProduct === null || primaryProduct === void 0 ? void 0 : primaryProduct.id]);
  return /*#__PURE__*/React.createElement(SearchDialogProduct, _extends({
    expandedStateInputPlaceholder: intl.formatMessage(messages.bitbucket_expanded_input_placeholder),
    generateAdvancedSearchUrl: generateAdvancedSearchUrl,
    id: BITBUCKET_PRODUCT_ID,
    permissionSupplier: permissionSupplier,
    preQuerySectionTitleGenerator: function preQuerySectionTitleGenerator(section) {
      return section.id === BitbucketScope.BitbucketRepostitory ? intl.formatMessage(messages.bitbucket_recent_repositories) : '';
    },
    sections: sections,
    title: intl.formatMessage(commonMessages.bitbucket_tab_label),
    urlGeneratorForNoResultsScreen: urlGeneratorForNoResultsScreen
  }, rest, itemSuppliers), function (_ref4) {
    var linkComponent = _ref4.linkComponent,
      query = _ref4.query;
    var codeSearchUrl = currentContext ? codeSearchContextualUrlGenerator(query, currentContext) : codeSearchUrlGenerator(query);
    var contextualSearchMessage = formattedSearchForCodeLabel(currentContext);
    return {
      Header: function Header() {
        return /*#__PURE__*/React.createElement(AdvancedSearch, {
          advancedSearchMessage: contextualSearchMessage,
          advancedSearchUrl: codeSearchUrl,
          linkComponent: linkComponent
        });
      },
      Footer: function Footer() {
        return /*#__PURE__*/React.createElement(React.Fragment, null, showAtlassianHomeSearchLink && /*#__PURE__*/React.createElement(SearchFooter, null, /*#__PURE__*/React.createElement(AtlassianHomeSearchLink, {
          orgId: orgId,
          cloudId: cloudId,
          showModifierIcon: true
        })));
      }
    };
  });
};

/**
 * The canonical implementation of a tab to show Bitbucket search results inside of the search dialog
 *
 * Can be imported across multiple Atlassian products and will show Bitbucket results there
 */
export var BitbucketTab = injectIntl(BitbucketTabWithIntl);